import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../services/authService';
import ChartRenderer from './ChartRenderer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ExploreData = () => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState(null);
  const [mode, setMode] = useState('natural'); // 'natural' or 'sql'

  // Session management state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showSessionSidebar, setShowSessionSidebar] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load DB schema and sessions on mount
  useEffect(() => {
    loadSchema();
    loadSessions();
  }, []);

  // Load messages when session changes
  useEffect(() => {
    if (currentSessionId) {
      loadSessionMessages(currentSessionId);
    }
  }, [currentSessionId]);

  const loadSchema = async () => {
    try {
      const response = await axios.get(`${API_URL}/db-schema`);
      setSchema(response.data);
    } catch (err) {
      console.error('Failed to load schema:', err);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await api.get('/api/chat/sessions');
      setSessions(response.data.sessions || []);

      // Auto-select most recent session or create new one
      if (response.data.sessions && response.data.sessions.length > 0) {
        setCurrentSessionId(response.data.sessions[0].id);
      } else {
        // Create first session automatically
        await createNewSession();
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // If sessions API fails, create a local-only session
      initializeLocalSession();
    }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      setLoadingHistory(true);
      const response = await api.get(`/api/chat/sessions/${sessionId}/messages`);

      // Convert backend format to frontend format
      const loadedMessages = response.data.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        // Spread all saved fields (rows, columns, count, sql, chart_data, etc.)
        data: msg.has_table_data && msg.table_data ? msg.table_data : undefined,
        has_table_data: msg.has_table_data
      }));

      setMessages(loadedMessages.length > 0 ? loadedMessages : [getWelcomeMessage()]);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([getWelcomeMessage()]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await api.post('/api/chat/sessions', {
        title: `Chat - ${new Date().toLocaleString()}`
      });

      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.id);
      setMessages([getWelcomeMessage()]);

      return newSession;
    } catch (err) {
      console.error('Failed to create session:', err);
      initializeLocalSession();
    }
  };

  const initializeLocalSession = () => {
    // Fallback: use local-only session if backend fails
    setCurrentSessionId('local');
    setMessages([getWelcomeMessage()]);
  };

  const getWelcomeMessage = () => ({
    role: 'assistant',
    content: "Welcome to Data Explorer. Ask me anything about the cement experiments database, or use SQL mode for direct queries.",
    timestamp: new Date()
  });

  const saveMessageToBackend = async (sessionId, role, content, tableData = null) => {
    if (sessionId === 'local') return; // Skip if local-only session

    try {
      await api.post(`/api/chat/sessions/${sessionId}/messages`, {
        role,
        content,
        has_table_data: !!tableData,
        table_data: tableData
      });
    } catch (err) {
      console.error('Failed to save message:', err);
      // Don't block the UI if save fails
    }
  };

  // Handle suggested action click (e.g., View Raw Data) - shows table inline
  const handleSuggestedAction = async (action, messageIndex) => {
    if (action.type === 'view_raw_data' && action.sql) {
      // Show loading state inline
      setMessages(prev => prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, loadingAction: true } : msg
      ));

      try {
        // Call the backend with mode=sql to execute the SQL directly
        const response = await api.post('/query-data', {
          query: action.sql,
          mode: 'sql'
        });

        // Process the response
        const aiMessage = {
          role: 'assistant',
          content: response.data.result || `Here are the ${response.data.count || 0} experiments:`,
          timestamp: new Date(),
          data: response.data
        };

        setMessages(prev => prev.filter(m => !m.isThinking).concat([aiMessage]));

        // Save to backend if we have a session
        if (currentSessionId) {
          await saveMessageToBackend(currentSessionId, 'user', action.label);
          await saveMessageToBackend(currentSessionId, 'assistant', aiMessage.content, response.data);
        }
      } catch (err) {
        console.error('Error executing suggested action:', err);
        setMessages(prev => prev.map((msg, idx) =>
          idx === messageIndex ? { ...msg, loadingAction: false, actionError: true } : msg
        ));
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const queryText = input.trim();
    const userMessage = {
      role: 'user',
      content: queryText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Add "thinking" indicator message
    const thinkingMessage = {
      role: 'assistant',
      content: 'AI Agent is working....',
      isThinking: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thinkingMessage]);

    // Save user message to backend
    if (currentSessionId) {
      await saveMessageToBackend(currentSessionId, 'user', queryText);
    }

    try {
      const endpoint = mode === 'natural' ? '/query-data' : '/query-sql';

      // Build conversation history for natural language mode.
      // Rules:
      //  - Skip thinking indicators, errors, and the static welcome message
      //  - For table results: send a compact summary (not the rows) so the
      //    agent has context without wasting tokens on raw data
      //  - Keep last 8 meaningful messages (≈ 4 exchanges)
      const conversationHistory = mode === 'natural'
        ? messages
          .filter(msg =>
            !msg.isThinking &&
            !msg.error &&
            msg.content &&
            !msg.content.startsWith('Welcome to Data Explorer')
          )
          .slice(-8)
          .map(msg => ({
            role: msg.role,
            content: msg.has_table_data
              ? `[Query returned table: ${msg.data?.count ?? '?'} rows × ${msg.data?.columns?.length ?? '?'} columns${msg.data?.sql ? ` | SQL: ${msg.data.sql.slice(0, 120)}` : ''}]`
              : msg.content
          }))
        : [];

      const payload = mode === 'natural'
        ? {
          query: queryText,
          conversation_history: conversationHistory
        }
        : { sql: queryText, limit: 100 };

      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        timeout: 120000 // 2 minutes
      });

      // Check if it needs clarification
      if (response.data.needs_clarification) {
        const clarificationMessage = {
          role: 'assistant',
          content: response.data.clarification,
          needsClarification: true,
          timestamp: new Date()
        };
        // Remove thinking message and add clarification
        setMessages(prev => prev.filter(m => !m.isThinking).concat(clarificationMessage));

        // Save clarification to backend
        if (currentSessionId) {
          await saveMessageToBackend(currentSessionId, 'assistant', response.data.clarification);
        }
      } else {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.result || 'Query executed successfully',
          data: response.data,
          timestamp: new Date()
        };
        // Remove thinking message and add real response
        setMessages(prev => prev.filter(m => !m.isThinking).concat(assistantMessage));

        // Save assistant message with table data to backend
        if (currentSessionId) {
          // The backend returns rows/columns/count/sql at the TOP LEVEL of response.data
          const tableData = response.data.rows ? {
            rows: response.data.rows,
            columns: response.data.columns,
            count: response.data.count,
            sql: response.data.sql,
            truncated: response.data.truncated || false,
            chart_data: response.data.chart_data || null
          } : response.data.chart_data ? {
            chart_data: response.data.chart_data
          } : null;

          await saveMessageToBackend(
            currentSessionId,
            'assistant',
            response.data.result || 'Query executed successfully',
            tableData
          );
        }
      }
    } catch (err) {
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${err.response?.data?.detail || err.message || 'Query failed'}`,
        error: true,
        timestamp: new Date()
      };
      // Remove thinking message and add error
      setMessages(prev => prev.filter(m => !m.isThinking).concat(errorMessage));

      // Save error to backend
      if (currentSessionId) {
        await saveMessageToBackend(currentSessionId, 'assistant', errorMessage.content);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleClearChat = async () => {
    if (window.confirm('Start a new chat? Current conversation will be saved.')) {
      await createNewSession();
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm('Delete this chat session? This cannot be undone.')) {
      try {
        await api.delete(`/api/chat/sessions/${sessionId}`);

        // Reload sessions
        const response = await api.get('/api/chat/sessions');
        setSessions(response.data.sessions || []);

        // If we deleted the current session, create a new one
        if (sessionId === currentSessionId) {
          if (response.data.sessions && response.data.sessions.length > 0) {
            setCurrentSessionId(response.data.sessions[0].id);
          } else {
            await createNewSession();
          }
        }
      } catch (err) {
        alert('Failed to delete session');
        console.error(err);
      }
    }
  };

  const renderMessageContent = (message, messageIdx) => {

    // ── Inline markdown helpers (defined first so all branches can use them) ──
    const renderInline = (text) => {
      const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`'))
          return <code key={i} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: '3px', fontSize: '0.85em', fontFamily: 'monospace' }}>{part.slice(1, -1)}</code>;
        return part;
      });
    };

    const formatAnalyticalText = (text) => {
      // Pre-process: expand inline ' - item' bullets to real newlines
      let processed = text
        .replace(/([^\n])(Key observations:|Summary:|Results?:)/gi, '$1\n$2')
        .replace(/ - (?=[A-Za-z*\[])/g, '\n- ');

      const paragraphs = processed.split(/\n{2,}/).filter(p => p.trim());

      return paragraphs.map((para, idx) => {
        const lines = para.split('\n').filter(l => l !== undefined);
        const bulletLines = lines.filter(l => /^\s*[-*]\s/.test(l));
        const nonBulletLines = lines.filter(l => !/^\s*[-*]\s/.test(l));

        if (bulletLines.length > 0) {
          return (
            <div key={idx} style={{ marginBottom: '10px' }}>
              {nonBulletLines.map((line, i) => line.trim() && (
                <p key={i} style={{ margin: '0 0 6px', fontSize: '14px', lineHeight: '1.6' }}>
                  {renderInline(line)}
                </p>
              ))}
              <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.7' }}>
                {bulletLines.map((item, i) => (
                  <li key={i} style={{ marginBottom: '3px' }}>
                    {renderInline(item.replace(/^\s*[-*]\s/, ''))}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        if (para.includes('📊') || para.includes('💡') || para.includes('⚠️') || para.includes('✓')) {
          return (
            <div key={idx} style={{
              background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: '6px', padding: '10px 12px', margin: '8px 0',
              fontSize: '14px', lineHeight: '1.6'
            }}>
              {lines.map((line, i) => (
                <div key={i} style={{ marginBottom: i < lines.length - 1 ? '3px' : 0 }}>
                  {renderInline(line)}
                </div>
              ))}
            </div>
          );
        }

        if (lines.length === 1 && /^\*\*.+\*\*$/.test(para.trim())) {
          return (
            <p key={idx} style={{ margin: '12px 0 4px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
              {renderInline(para.trim())}
            </p>
          );
        }

        return (
          <p key={idx} style={{ margin: '0 0 10px', fontSize: '14px', lineHeight: '1.7' }}>
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                {renderInline(line)}
                {i < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      });
    };

    // Thinking indicator
    if (message.isThinking) {
      return <div className="thinking-indicator">{message.content}</div>;
    }

    // Error message
    if (message.error) {
      return <div className="message-error">{message.content}</div>;
    }

    // Clarification request
    if (message.needsClarification) {
      return (
        <div className="message-clarification">
          <div className="clarification-text">{message.content}</div>
          <div className="clarification-hint">
            Try being more specific with column names, ranges, or locations.
          </div>
        </div>
      );
    }

    // Table data result - enhanced styling
    if (message.data?.rows && message.data?.columns) {
      return (
        <div className="message-table-result">
          {/* Text summary if exists */}
          {message.content && message.content.trim() && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: '#f9fafb',
              borderRadius: '6px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {formatAnalyticalText(message.content)}
            </div>
          )}

          {/* Table section with clear header */}
          <div style={{
            background: '#fff',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            marginTop: '12px'
          }}>
            {/* Table header with metadata */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              padding: '12px 16px',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                📊 Query Results
              </div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                {message.data.count} rows × {message.data.columns.length} columns
                {message.data.truncated && ' (limited to 100)'}
              </div>
            </div>

            {/* SQL Query collapsible */}
            {message.data.sql && (
              <details style={{
                padding: '8px 16px',
                background: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
                cursor: 'pointer'
              }}>
                <summary style={{
                  color: '#2563eb',
                  fontWeight: '500',
                  fontSize: '13px',
                  padding: '4px 0'
                }}>
                  View SQL Query
                </summary>
                <pre style={{
                  background: '#1f2937',
                  color: '#f3f4f6',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  marginTop: '8px',
                  overflow: 'auto',
                  fontFamily: 'monospace'
                }}>
                  {message.data.sql}
                </pre>
              </details>
            )}

            {/* Scrollable table container */}
            <div style={{
              overflowX: 'auto',
              maxHeight: '500px',
              overflowY: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  background: '#f9fafb',
                  zIndex: 10
                }}>
                  <tr>
                    {message.data.columns.map((col, idx) => (
                      <th key={idx} style={{
                        padding: '10px 12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e5e7eb',
                        fontWeight: '600',
                        color: '#1f2937',
                        whiteSpace: 'nowrap'
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {message.data.rows.map((row, idx) => (
                    <tr key={idx} style={{
                      background: idx % 2 === 0 ? '#fff' : '#f9fafb',
                      transition: 'background 0.15s'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f9fafb'}
                    >
                      {message.data.columns.map((col, colIdx) => (
                        <td key={colIdx} style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #e5e7eb',
                          color: '#374151',
                          whiteSpace: 'nowrap'
                        }}>
                          {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {message.data.execution_time_seconds && (
            <div className="execution-time-badge" style={{ marginTop: '12px' }}>
              Execution time: {message.data.execution_time_seconds}s
            </div>
          )}
        </div>
      );
    }

    // Analytical response - enhanced formatting
    const content = message.content || '';

    return (
      <div className="message-text">
        {formatAnalyticalText(content)}
        {/* Render chart if chart_data exists */}
        {message.data?.chart_data && (
          <ChartRenderer chartData={message.data.chart_data} />
        )}
        {/* Render suggested actions if available */}
        {message.data?.suggested_actions && message.data.suggested_actions.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <div style={{
              fontSize: '13px',
              color: '#0369a1',
              marginBottom: '10px',
              fontWeight: '500'
            }}>
              💡 Suggested Actions:
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {message.data.suggested_actions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedAction(action, messageIdx)}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.2)';
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatSessionTitle = (session) => {
    if (session.title && session.title !== 'New Chat') {
      return session.title;
    }
    return `Chat ${new Date(session.created_at).toLocaleDateString()}`;
  };

  return (
    <div className="explore-data-chat-container">
      <div className="chat-header">
        <div className="header-title">
          <h2>Data Explorer Chat</h2>
          <p>{schema?.row_count || '...'} experiments available</p>
        </div>

        <div className="header-controls">
          <div className="mode-toggle">
            <button
              className={`mode-button ${mode === 'natural' ? 'active' : ''}`}
              onClick={() => setMode('natural')}
              title="Natural Language"
            >
              Chat
            </button>
            <button
              className={`mode-button ${mode === 'sql' ? 'active' : ''}`}
              onClick={() => setMode('sql')}
              title="SQL Mode"
            >
              SQL
            </button>
          </div>

          <button
            className="session-button"
            onClick={() => setShowSessionSidebar(!showSessionSidebar)}
            title="View chat history"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            History
          </button>

          <button className="clear-button" onClick={handleClearChat} title="Start new chat">
            New Chat
          </button>
        </div>
      </div>

      <div className="chat-body">
        {/* Session Sidebar */}
        {showSessionSidebar && (
          <>
            <div className="session-sidebar-overlay" onClick={() => setShowSessionSidebar(false)} />
            <div className="session-sidebar">
              <div className="session-sidebar-header">
                <h3>Chat History</h3>
                <button onClick={() => setShowSessionSidebar(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="session-list">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`session-item ${session.id === currentSessionId ? 'session-item-active' : ''}`}
                    onClick={() => {
                      setCurrentSessionId(session.id);
                      setShowSessionSidebar(false);
                    }}
                  >
                    <div className="session-title">{formatSessionTitle(session)}</div>
                    <div className="session-meta">
                      {session.message_count || 0} messages • {new Date(session.updated_at).toLocaleDateString()}
                    </div>
                    <button
                      className="session-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(session.id);
                      }}
                      title="Delete session"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Messages Area */}
        <div className="chat-messages">
          {loadingHistory ? (
            <div className="loading-history">
              <div className="spinner"></div>
              <p>Loading chat history...</p>
            </div>
          ) : (
            <>
              {messages.map((message, idx) => (
                <div key={idx} className={`message ${message.role}`}>
                  <div className="message-avatar">
                    {message.role === 'user' ? 'U' : 'AI'}
                  </div>
                  <div className="message-bubble">
                    {renderMessageContent(message, idx)}
                    <div className="message-timestamp">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="chat-input-container">
        <form className="query-form" onSubmit={handleSend}>
          <textarea
            ref={inputRef}
            className="query-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={mode === 'natural' ? "Ask a question about the experiments..." : "Enter SQL query..."}
            disabled={loading}
            rows={2}
          />
          <button
            type="submit"
            className="send-button"
            disabled={loading || !input.trim()}
          >
            {loading ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExploreData;
